import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, User, Calendar, FileText, ChevronRight, Car, BookOpen, Download, Database } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useFirebase } from "@/contexts/FirebaseContext";
import { collection, getDocs, query, where, orderBy, limit } from "firebase/firestore";
import { format } from "date-fns";
import JSZip from "jszip";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import { useToast } from "@/hooks/use-toast";

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  createdAt: string;
  date?: string;
}

const AdminDashboard = () => {
  const { getFirestore } = useFirebase();
  const { toast } = useToast();
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalInstructors: 0,
  });
  const [recentRegistrations, setRecentRegistrations] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [isGeneratingBackup, setIsGeneratingBackup] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const db = getFirestore();
        
        // Fetch total students and instructors
        const usersSnapshot = await getDocs(collection(db, "users"));
        const users = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as any[];
        
        const totalStudents = users.filter(user => user.role === "student").length;
        const totalInstructors = users.filter(user => user.role === "instructor").length;
        
        setStats({
          totalStudents,
          totalInstructors,
        });

        // Fetch recent registrations (students who registered for courses)
        const enrollmentsSnapshot = await getDocs(
          query(
            collection(db, "enrollments"),
            orderBy("createdAt", "desc"),
            limit(5)
          )
        );

        const recentEnrollments = await Promise.all(
          enrollmentsSnapshot.docs.map(async (doc) => {
            const enrollment = doc.data();
            
            // Handle createdAt date safely
            let createdAtDate: Date;
            try {
              if (enrollment.createdAt) {
                // Handle Firestore timestamp objects
                if (enrollment.createdAt.seconds) {
                  createdAtDate = new Date(enrollment.createdAt.seconds * 1000);
                } else if (typeof enrollment.createdAt === 'string') {
                  createdAtDate = new Date(enrollment.createdAt);
                } else {
                  createdAtDate = new Date();
                }
              } else {
                createdAtDate = new Date();
              }
            } catch (error) {
              createdAtDate = new Date();
            }
            
            // Skip if studentId is undefined
            if (!enrollment.studentId) {
              // Handle public bookings that might have different field names
              const publicStudentId = enrollment.userId || enrollment.studentId;
              if (!publicStudentId) {
                return {
                  id: doc.id,
                  name: enrollment.studentName || enrollment.firstName || "Unknown Student",
                  email: enrollment.studentEmail || enrollment.email || "",
                  role: "student",
                  createdAt: enrollment.createdAt || new Date().toISOString(),
                  date: format(new Date(), "dd MMM yyyy"),
                } as User;
              }
            }
            
            // Find student data from the already fetched users
            const studentData = users.find(user => user.id === enrollment.studentId);
            
            // If no student data found, try to use enrollment data for public bookings
            if (!studentData && enrollment.studentName) {
              return {
                id: enrollment.studentId || enrollment.userId || doc.id,
                name: enrollment.studentName || `${enrollment.firstName || ''} ${enrollment.lastName || ''}`.trim() || "Unknown Student",
                email: enrollment.studentEmail || enrollment.email || "",
                role: "student",
                createdAt: createdAtDate.toISOString(),
                date: format(createdAtDate, "dd MMM yyyy"),
              } as User;
            }
            
            return {
              id: enrollment.studentId,
              name: studentData ? `${studentData.firstName || ''} ${studentData.lastName || ''}`.trim() || "Unknown Student" : "Unknown Student",
              email: studentData?.email || "",
              role: "student",
              createdAt: createdAtDate.toISOString(),
              date: format(createdAtDate, "dd MMM yyyy"),
            } as User;
          })
        );

        setRecentRegistrations(recentEnrollments);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getFirestore]);

  // Comprehensive Backup Function
  const generateSystemBackup = async () => {
    setIsGeneratingBackup(true);
    try {
      const db = getFirestore();
      const zip = new JSZip();
      
      // Create folders for different data types
      const pdfFolder = zip.folder("PDF_Reports");
      const excelFolder = zip.folder("Excel_Data");
      
      // Fetch all data from Firestore
      const collections = [
        "users",
        "courses", 
        "enrollments",
        "schedules",
        "lessons",
        "payments",
        "instructor_availability"
      ];
      
      const allData: { [key: string]: any[] } = {};
      
      // Fetch data from all collections
      for (const collectionName of collections) {
        try {
          const snapshot = await getDocs(collection(db, collectionName));
          allData[collectionName] = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (error) {
          console.error(`Error fetching ${collectionName}:`, error);
          allData[collectionName] = [];
        }
      }
      
      // Generate Excel files for each collection
      for (const [collectionName, data] of Object.entries(allData)) {
        if (data.length > 0) {
          const worksheet = XLSX.utils.json_to_sheet(data);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, collectionName);
          
          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          excelFolder?.file(`${collectionName}.xlsx`, excelBuffer);
        }
      }
      
      // Generate comprehensive Excel report
      const comprehensiveData = {
        summary: {
          totalStudents: allData.users.filter(u => u.role === 'student').length,
          totalInstructors: allData.users.filter(u => u.role === 'instructor').length,
          totalCourses: allData.courses.length,
          totalEnrollments: allData.enrollments.length,
          totalSchedules: allData.schedules.length,
          totalLessons: allData.lessons.length,
          totalPayments: allData.payments.length,
          backupDate: new Date().toISOString()
        },
        students: allData.users.filter(u => u.role === 'student').map(student => ({
          id: student.id,
          name: `${student.firstName || ''} ${student.lastName || ''}`.trim(),
          email: student.email,
          phone: student.phone,
          address: student.address,
          postcode: student.postcode,
          gearbox: student.gearbox,
          licenseNumber: student.licenseNumber,
          createdAt: student.createdAt,
          enrollments: allData.enrollments.filter(e => e.studentId === student.id).length,
          schedules: allData.schedules.filter(s => s.studentId === student.id).length,
          completedLessons: allData.lessons.filter(l => l.studentId === student.id && l.status === 'completed').length
        })),
        instructors: allData.users.filter(u => u.role === 'instructor').map(instructor => ({
          id: instructor.id,
          name: `${instructor.firstName || ''} ${instructor.lastName || ''}`.trim(),
          email: instructor.email,
          phone: instructor.phone,
          specializations: instructor.specializations,
          createdAt: instructor.createdAt,
          students: allData.enrollments.filter(e => e.instructorId === instructor.id).length,
          schedules: allData.schedules.filter(s => s.instructorId === instructor.id).length
        })),
        courses: allData.courses.map(course => ({
          id: course.id,
          title: course.title,
          type: course.type,
          subtype: course.subtype,
          totalHours: course.totalHours,
          price: course.price,
          vehicleType: course.vehicleType,
          licenseType: course.licenseType,
          enrollments: allData.enrollments.filter(e => e.courseId === course.id).length
        })),
        enrollments: allData.enrollments.map(enrollment => {
          const student = allData.users.find(u => u.id === enrollment.studentId);
          const instructor = allData.users.find(u => u.id === enrollment.instructorId);
          const course = allData.courses.find(c => c.id === enrollment.courseId);
          return {
            id: enrollment.id,
            studentName: student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Unknown',
            studentEmail: student?.email || 'Unknown',
            instructorName: instructor ? `${instructor.firstName || ''} ${instructor.lastName || ''}`.trim() : 'Unknown',
            instructorEmail: instructor?.email || 'Unknown',
            courseName: course?.title || 'Unknown',
            courseType: course?.type || 'Unknown',
            totalHours: enrollment.totalHours,
            hoursTaken: enrollment.hoursTaken,
            hoursRemaining: enrollment.hoursRemaining,
            status: enrollment.status,
            createdAt: enrollment.createdAt
          };
        }),
        schedules: allData.schedules.map(schedule => {
          const student = allData.users.find(u => u.id === schedule.studentId);
          const instructor = allData.users.find(u => u.id === schedule.instructorId);
          return {
            id: schedule.id,
            studentName: student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Unknown',
            instructorName: instructor ? `${instructor.firstName || ''} ${instructor.lastName || ''}`.trim() : 'Unknown',
            date: schedule.date,
            startTime: schedule.startTime,
            endTime: schedule.endTime,
            duration: schedule.duration,
            status: schedule.status,
            venue: schedule.venue,
            createdAt: schedule.createdAt
          };
        }),
        payments: allData.payments.map(payment => {
          const student = allData.users.find(u => u.id === payment.studentId);
          return {
            id: payment.id,
            studentName: student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Unknown',
            amount: payment.amount,
            currency: payment.currency,
            status: payment.status,
            paymentMethod: payment.paymentMethod,
            createdAt: payment.createdAt
          };
        })
      };
      
      // Create comprehensive Excel report
      const comprehensiveWorksheet = XLSX.utils.json_to_sheet([comprehensiveData.summary]);
      const comprehensiveWorkbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, comprehensiveWorksheet, "Summary");
      
      // Add detailed sheets
      const studentsWorksheet = XLSX.utils.json_to_sheet(comprehensiveData.students);
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, studentsWorksheet, "Students");
      
      const instructorsWorksheet = XLSX.utils.json_to_sheet(comprehensiveData.instructors);
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, instructorsWorksheet, "Instructors");
      
      const coursesWorksheet = XLSX.utils.json_to_sheet(comprehensiveData.courses);
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, coursesWorksheet, "Courses");
      
      const enrollmentsWorksheet = XLSX.utils.json_to_sheet(comprehensiveData.enrollments);
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, enrollmentsWorksheet, "Enrollments");
      
      const schedulesWorksheet = XLSX.utils.json_to_sheet(comprehensiveData.schedules);
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, schedulesWorksheet, "Schedules");
      
      const paymentsWorksheet = XLSX.utils.json_to_sheet(comprehensiveData.payments);
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, paymentsWorksheet, "Payments");
      
      const comprehensiveBuffer = XLSX.write(comprehensiveWorkbook, { bookType: 'xlsx', type: 'array' });
      excelFolder?.file("Comprehensive_Report.xlsx", comprehensiveBuffer);
      
      // Generate PDF reports
      const generatePDFReport = async (title: string, data: any[], filename: string) => {
        const reportHTML = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>${title}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { 
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                margin: 0; 
                padding: 20px; 
                background: white; 
                color: #333;
              }
              .header { 
                text-align: center; 
                margin-bottom: 30px; 
                padding: 20px; 
                background: linear-gradient(135deg, #1e40af, #3b82f6);
                color: white;
                border-radius: 10px;
              }
              .header h1 { font-size: 28px; margin-bottom: 10px; }
              .header p { font-size: 14px; opacity: 0.9; }
              .summary { 
                margin-bottom: 30px; 
                padding: 20px; 
                background: #f8fafc; 
                border-radius: 8px;
                border-left: 4px solid #3b82f6;
              }
              .summary h2 { color: #1e40af; margin-bottom: 15px; }
              .summary-grid { 
                display: grid; 
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); 
                gap: 15px; 
              }
              .summary-item { 
                background: white; 
                padding: 15px; 
                border-radius: 6px; 
                box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              }
              .summary-item h3 { color: #64748b; font-size: 12px; margin-bottom: 5px; }
              .summary-item p { font-size: 24px; font-weight: bold; color: #1e40af; }
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin-bottom: 30px; 
                background: white;
                box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                border-radius: 8px;
                overflow: hidden;
              }
              th { 
                background: #1e40af; 
                color: white; 
                padding: 12px; 
                text-align: left; 
                font-weight: 600;
              }
              td { 
                padding: 12px; 
                border-bottom: 1px solid #e2e8f0; 
              }
              tr:nth-child(even) { background: #f8fafc; }
              tr:hover { background: #f1f5f9; }
              .footer { 
                margin-top: 40px; 
                text-align: center; 
                padding: 20px; 
                color: #64748b; 
                font-size: 12px;
                border-top: 1px solid #e2e8f0;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${title}</h1>
              <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
            </div>
            
            <div class="summary">
              <h2>System Summary</h2>
              <div class="summary-grid">
                <div class="summary-item">
                  <h3>Total Students</h3>
                  <p>${comprehensiveData.summary.totalStudents}</p>
                </div>
                <div class="summary-item">
                  <h3>Total Instructors</h3>
                  <p>${comprehensiveData.summary.totalInstructors}</p>
                </div>
                <div class="summary-item">
                  <h3>Total Courses</h3>
                  <p>${comprehensiveData.summary.totalCourses}</p>
                </div>
                <div class="summary-item">
                  <h3>Total Enrollments</h3>
                  <p>${comprehensiveData.summary.totalEnrollments}</p>
                </div>
                <div class="summary-item">
                  <h3>Total Schedules</h3>
                  <p>${comprehensiveData.summary.totalSchedules}</p>
                </div>
                <div class="summary-item">
                  <h3>Total Lessons</h3>
                  <p>${comprehensiveData.summary.totalLessons}</p>
                </div>
                <div class="summary-item">
                  <h3>Total Payments</h3>
                  <p>${comprehensiveData.summary.totalPayments}</p>
                </div>
              </div>
            </div>
            
            <table>
              <thead>
                <tr>
                  ${Object.keys(data[0] || {}).map(key => `<th>${key.charAt(0).toUpperCase() + key.slice(1)}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${data.map(row => `
                  <tr>
                    ${Object.values(row).map(value => `<td>${value || 'N/A'}</td>`).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="footer">
              <p>DriveWise School Management System - Complete Backup Report</p>
              <p>This report contains all system data as of ${new Date().toLocaleDateString()}</p>
            </div>
          </body>
          </html>
        `;
        
        // Create HTML file for PDF conversion
        const htmlBlob = new Blob([reportHTML], { type: 'text/html' });
        pdfFolder?.file(filename.replace('.pdf', '.html'), htmlBlob);
      };
      
      // Generate PDF reports for each data type
      await generatePDFReport("Students Report", comprehensiveData.students, "Students_Report.pdf");
      await generatePDFReport("Instructors Report", comprehensiveData.instructors, "Instructors_Report.pdf");
      await generatePDFReport("Courses Report", comprehensiveData.courses, "Courses_Report.pdf");
      await generatePDFReport("Enrollments Report", comprehensiveData.enrollments, "Enrollments_Report.pdf");
      await generatePDFReport("Schedules Report", comprehensiveData.schedules, "Schedules_Report.pdf");
      await generatePDFReport("Payments Report", comprehensiveData.payments, "Payments_Report.pdf");
      
      // Generate the ZIP file
      const zipBlob = await zip.generateAsync({ type: "blob" });
      
      // Download the ZIP file
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `drivewise_backup_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Backup Generated Successfully",
        description: "All system data has been exported in PDF and Excel formats.",
      });
      
    } catch (error) {
      console.error("Error generating backup:", error);
      toast({
        title: "Backup Failed",
        description: "There was an error generating the backup. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingBackup(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="admin">
        <div className="flex items-center justify-center h-64">
          <p>Loading dashboard data...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="admin">
      <div className="mb-8 space-y-4">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-600">
          Manage users, lessons, and system settings in one place.
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-6 mb-8 grid-cols-1 sm:grid-cols-2">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="mr-4 bg-blue-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Total Students</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.totalStudents}</h3>
              </div>
              <Button variant="ghost" size="icon" className="ml-auto" asChild>
                <Link to="/admin-dashboard/users">
                  <ChevronRight className="h-5 w-5" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="mr-4 bg-caution-100 p-3 rounded-full">
                <Car className="h-6 w-6 text-caution-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Instructors</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.totalInstructors}</h3>
              </div>
              <Button variant="ghost" size="icon" className="ml-auto" asChild>
                <Link to="/admin-dashboard/instructors">
                  <ChevronRight className="h-5 w-5" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 mb-8 grid-cols-1 lg:grid-cols-2">
        {/* Recent Registrations */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Recent Course Registrations</CardTitle>
            <CardDescription>Students who recently enrolled in courses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentRegistrations.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center p-3 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="mr-4 bg-gray-100 p-2 rounded-full">
                    <User className="h-5 w-5 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h3 className="font-medium text-gray-900">{user.name}</h3>
                      <Badge className="ml-3 bg-blue-500">student</Badge>
                    </div>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                  <div className="text-sm text-gray-500">{user.date}</div>
                </div>
              ))}
              <Button variant="outline" className="w-full" asChild>
                <Link to="/admin-dashboard/users">View All Users</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Quick Actions</CardTitle>
            <CardDescription>Common administrative tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Button className="justify-start h-auto py-4" asChild>
                <Link to="/admin-dashboard/users/new">
                  <User className="h-5 w-5 mr-2" />
                  <div className="text-left">
                    <div className="font-medium">Add New User</div>
                    <div className="text-xs text-white/80">Create student or instructor</div>
                  </div>
                </Link>
              </Button>

              <Button 
                className="justify-start h-auto py-4 bg-green-600 hover:bg-green-700" 
                onClick={generateSystemBackup}
                disabled={isGeneratingBackup}
              >
                <Database className="h-5 w-5 mr-2" />
                <div className="text-left">
                  <div className="font-medium">
                    {isGeneratingBackup ? 'Generating Backup...' : 'Generate System Backup'}
                  </div>
                  <div className="text-xs text-white/80">
                    {isGeneratingBackup ? 'Please wait...' : 'Export all data (PDF + Excel)'}
                  </div>
                </div>
              </Button>

              <Button className="justify-start h-auto py-4 bg-caution-600 hover:bg-caution-700" asChild>
                <Link to="/admin-dashboard/bookings/new">
                  <Calendar className="h-5 w-5 mr-2" />
                  <div className="text-left">
                    <div className="font-medium">Create Booking</div>
                    <div className="text-xs text-white/80">Schedule new lessons</div>
                  </div>
                </Link>
              </Button>

              <Button className="justify-start h-auto py-4 bg-blue-600 hover:bg-blue-700" asChild>
                <Link to="/admin-dashboard/content/manage">
                  <BookOpen className="h-5 w-5 mr-2" />
                  <div className="text-left">
                    <div className="font-medium">Manage Content</div>
                    <div className="text-xs text-white/80">Learning materials & website</div>
                  </div>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
